import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Check, ChevronDown, Star } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLibraryFonts, useCustomFonts, buildFontOptions } from "@/lib/fonts";
import { trackEvent } from "@/lib/analytics";

const PAGE_SIZE = 10;

// Shared, reusable font selector with a REAL visual preview per font, search,
// and lazy loading (max 10 shown initially). Records font analytics events.
export default function FontPicker({ value, onChange, testId = "font-picker", page, feature = "Fonts", sample = "The quick brown fox jumps over the lazy dog" }) {
  const library = useLibraryFonts();     // reactive: re-render when library loads
  const custom = useCustomFonts();       // reactive: user uploads
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(PAGE_SIZE);
  const searchTimer = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const options = useMemo(() => buildFontOptions(), [library, custom]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) =>
      (o.name || "").toLowerCase().includes(term) ||
      (o.family || "").toLowerCase().includes(term) ||
      (o.category || "").toLowerCase().includes(term) ||
      (o.tags || []).some((t) => (t || "").toLowerCase().includes(term)));
  }, [q, options]);

  const visible = filtered.slice(0, limit);

  // Preload the actual FontFaces for the visible options so previews render correctly.
  useEffect(() => {
    if (!open || typeof document === "undefined" || !document.fonts) return;
    visible.forEach((o) => { try { document.fonts.load(`600 20px "${o.family}"`, sample.slice(0, 30)).catch(() => {}); } catch { /* noop */ } });
  }, [open, visible, sample]);

  // Record a font_search event (debounced) whenever the user searches.
  const onSearch = (val) => {
    setQ(val);
    setLimit(PAGE_SIZE);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const term = val.trim();
    if (!term) return;
    searchTimer.current = setTimeout(() => {
      const count = options.filter((o) =>
        (o.name || "").toLowerCase().includes(term.toLowerCase()) ||
        (o.family || "").toLowerCase().includes(term.toLowerCase()) ||
        (o.category || "").toLowerCase().includes(term.toLowerCase()) ||
        (o.tags || []).some((t) => (t || "").toLowerCase().includes(term.toLowerCase()))).length;
      trackEvent({ eventType: "font_search", page, feature, action: `Searched font: ${term}`,
        target: term, metadata: { searchTerm: term, resultCount: count } });
    }, 600);
  };

  const onOpenChange = (o) => {
    setOpen(o);
    if (o) trackEvent({ eventType: "font_selector_open", page, feature, action: "Opened font selector" });
    else { setQ(""); setLimit(PAGE_SIZE); }
  };

  const pick = (fam) => {
    onChange(fam);
    trackEvent({ eventType: "font_select", page, feature, action: `Selected font: ${fam}`, target: fam });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId}
          className="flex w-full items-center justify-between rounded-lg border border-white/10 bg-[#1a1a1a] px-3 py-2 text-sm text-white outline-none hover:border-white/25">
          <span className="truncate" style={{ fontFamily: `"${value}", sans-serif` }}>{value || "Choose a font"}</span>
          <ChevronDown size={15} className="ml-2 shrink-0 text-[#A1A1AA]" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[320px] border-white/10 bg-[#111] p-0 text-white" data-testid={`${testId}-panel`}>
        <div className="border-b border-white/10 p-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
            <input autoFocus data-testid={`${testId}-search`} value={q} onChange={(e) => onSearch(e.target.value)}
              placeholder="Search fonts..." className="w-full rounded-md border border-white/10 bg-white/[0.03] py-2 pl-8 pr-3 text-sm outline-none focus:border-[#00F0FF]/50" />
          </div>
        </div>
        <div className="max-h-[320px] overflow-y-auto p-1" data-testid={`${testId}-list`}>
          {filtered.length === 0 ? (
            <div className="px-3 py-8 text-center text-sm text-[#A1A1AA]" data-testid={`${testId}-empty`}>No fonts found</div>
          ) : (
            <>
              {visible.map((o) => (
                <button key={o.family} type="button" data-testid={`${testId}-option-${o.family}`} onClick={() => pick(o.family)}
                  className={`group flex w-full items-start gap-2 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-white/5 ${value === o.family ? "bg-[#00F0FF]/10" : ""}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-[13px] font-semibold text-white" style={{ fontFamily: `"${o.family}", sans-serif` }}>{o.name}</span>
                      {o.featured && <Star size={11} className="shrink-0 fill-[#FFD400] text-[#FFD400]" />}
                      {o.telugu && <span data-testid={`${testId}-telugu-badge-${o.family}`} title="Has Telugu letters" className="shrink-0 rounded bg-[#00F0FF]/15 px-1 text-[9px] font-bold uppercase text-[#00F0FF]">తె Telugu</span>}
                      {o.source === "custom" && <span className="shrink-0 rounded bg-white/10 px-1 text-[9px] uppercase text-[#A1A1AA]">yours</span>}
                    </div>
                    <div className="truncate text-[13px] text-white/70" style={{ fontFamily: `"${o.family}", sans-serif` }}>{sample}</div>
                  </div>
                  {value === o.family && <Check size={15} className="mt-1 shrink-0 text-[#00F0FF]" />}
                </button>
              ))}
              {filtered.length > limit && (
                <button type="button" data-testid={`${testId}-more`} onClick={() => setLimit((l) => l + PAGE_SIZE)}
                  className="mt-1 w-full rounded-md border border-white/10 py-2 text-xs font-semibold text-[#A1A1AA] hover:text-white">
                  Show {Math.min(PAGE_SIZE, filtered.length - limit)} more ({filtered.length - limit} left)
                </button>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
